import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight, Sun, Activity, FileText, Gauge, ShieldCheck, Cable,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BrandLogo, useSettings } from "../context/SettingsContext";

/**
 * Public marketing landing page served at `/`.
 * Logged-in users are silently redirected to `/dashboard`.
 *
 * Positioning: **metering + monitoring first**. Design import (SolarEdge,
 * PV*SOL) is present but framed as the starting point, not the headline —
 * the real value is what happens after commissioning: half-hourly meter
 * ingestion, actual-vs-modelled reconciliation, and shareable KPI reports.
 */
const FEATURES = [
  {
    icon: Activity,
    title: "Half-hourly metering, on autopilot",
    body:
      "MeterOnline (metermanager.co.uk) HH data pulled daily at 03:00 UTC — import, export and (optional) generation meters. Automatic 7-day gap detection plus on-demand historic backfill up to 1 year.",
  },
  {
    icon: Gauge,
    title: "Actual vs modelled, six overlays deep",
    body:
      "One chart, six toggleable series — actual, generation, import, export, design simulated and weather-based estimate — with variance % surfaced on live KPI cards. Toggle any series to isolate the signal that matters.",
  },
  {
    icon: Cable,
    title: "Multi-source generation ingest",
    body:
      "Import + export come from MeterOnline. Generation resolves down a priority chain: dedicated MeterOnline generation meter → SolarEdge Cloud → Solis Cloud (HMAC-signed). Mix vendors across your portfolio.",
  },
  {
    icon: Sun,
    title: "Design import as the starting point",
    body:
      "Kick off a project from a SolarEdge Designer URL, a PV*SOL yield report or a proposal PDF — Gemini-vision extracts kWp, arrays, monthly yield and address. Design metadata drives the modelled overlay on every chart.",
  },
  {
    icon: FileText,
    title: "Investor-ready CSV + PDF reports",
    body:
      "One-click CSV or WeasyPrint PDF of any site's monitoring chart — org logo, site metadata, KPI band, full time-series table. Landlords and IRR reviewers get the same view your ops team uses.",
  },
  {
    icon: ShieldCheck,
    title: "UK-first, self-hostable, audit-ready",
    body:
      "GBP-native pricing and VAT, MCS-ready document store, audit trail on every status transition, HMAC-signed webhooks to your CRM or Slack. Ships as a bash installer for any Debian/Ubuntu box.",
  },
];

const METRICS = [
  { value: "48/day",  label: "Half-hourly points per meter" },
  { value: "3 sources", label: "MeterOnline · SolarEdge · Solis" },
  { value: "03:00 UTC", label: "Daily sync + gap backfill" },
  { value: "100%",    label: "Self-hostable" },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const { settings } = useSettings();
  const brand = settings?.org_name || "Flow";

  // Once auth check resolves and we have a session, jump straight into the app.
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div
      className="min-h-screen w-full overflow-x-hidden"
      style={{
        background:
          "radial-gradient(1100px 700px at 85% -10%, rgba(22,163,155,0.12), transparent 60%), radial-gradient(900px 600px at -10% 110%, rgba(243,154,92,0.10), transparent 60%), #FFFFFF",
      }}
      data-testid="landing-page"
    >
      {/* ---------- Top bar ---------- */}
      <header className="px-6 md:px-12 lg:px-20 pt-8 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="landing-brand">
          <BrandLogo size={36} />
          <div className="font-display text-xl" style={{ color: "var(--ink)" }}>
            {brand}
          </div>
        </div>
        <nav className="flex items-center gap-4">
          <a
            href="#features"
            className="hidden md:inline-block text-sm muted hover:text-[color:var(--ink)] transition-colors"
            data-testid="landing-nav-features"
          >
            Features
          </a>
          <Link
            to="/login"
            className="text-sm font-semibold px-4 py-2 rounded-full border transition-colors"
            style={{ borderColor: "var(--line)", color: "var(--ink)" }}
            data-testid="landing-nav-signin"
          >
            Sign in
          </Link>
        </nav>
      </header>

      {/* ---------- Hero ---------- */}
      <section className="px-6 md:px-12 lg:px-20 pt-10 pb-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
        <div>
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest"
            style={{ background: "var(--surface-2)", color: "var(--accent-hover)" }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--accent)" }} />
            Half-hourly metering & monitoring · built for UK C&amp;I solar
          </div>
          <h1
            className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            style={{ color: "var(--ink)" }}
            data-testid="landing-hero-title"
          >
            Solar PV metering, monitoring and reporting — <span style={{ color: "var(--accent)" }}>on your own server.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg max-w-xl muted leading-relaxed">
            Flow ingests half-hourly meter data from MeterOnline, cross-references generation from SolarEdge or Solis, and reconciles it against your SolarEdge Designer or PV*SOL yield model — daily.
            One chart, six overlays, downloadable KPI reports for landlords and investors.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="landing-hero-ctas">
            <a
              href="mailto:hello@eneprox.co.uk?subject=FLOW%20-%20Book%20a%20demo"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white transition-all hover:translate-y-[-1px]"
              style={{ background: "var(--accent)", boxShadow: "var(--shadow-accent)" }}
              data-testid="landing-cta-demo"
            >
              Book a demo <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border transition-colors"
              style={{ borderColor: "var(--line)", color: "var(--ink)", background: "#fff" }}
              data-testid="landing-cta-signin"
            >
              Customer sign in
            </Link>
          </div>

          {/* Metric strip */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4" data-testid="landing-metrics">
            {METRICS.map((m) => (
              <div key={m.label}>
                <div
                  className="font-display text-2xl sm:text-3xl"
                  style={{ color: "var(--ink)" }}
                >
                  {m.value}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest mt-1 muted">
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hero visual — a layered card mockup using brand colours,
            avoids the AI-slop stock-photo trap. */}
        <div className="relative h-[460px] hidden lg:block" aria-hidden="true">
          <div
            className="absolute right-0 top-0 w-[420px] h-[320px] rounded-3xl rotate-[3deg]"
            style={{
              background: "linear-gradient(135deg, #314041 0%, #00D6DF 100%)",
              boxShadow: "var(--shadow-lg)",
            }}
          />
          <div
            className="absolute right-12 top-20 w-[420px] h-[260px] rounded-2xl bg-white p-6 flex flex-col gap-3"
            style={{ boxShadow: "var(--shadow-lg)", border: "1px solid var(--line)" }}
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest muted">
                Site · Viables Business Park · 100 kWp
              </span>
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase"
                style={{ background: "var(--accent-soft)", color: "var(--accent-hover)" }}
              >
                live · HH sync
              </span>
            </div>
            <div className="font-display text-2xl" style={{ color: "var(--ink)" }}>
              4,212 kWh <span className="text-sm muted">last 7 days</span>
            </div>
            {/* Fake bar chart */}
            <div className="flex items-end gap-1.5 h-24 mt-2">
              {[42, 65, 78, 88, 95, 72, 55, 48, 62, 80, 90, 70].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t"
                  style={{
                    height: `${h}%`,
                    background:
                      i % 3 === 0 ? "var(--accent)" : i % 3 === 1 ? "#314041" : "var(--warm)",
                    opacity: 0.85,
                  }}
                />
              ))}
            </div>
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest muted">
              <span>actual · 4,212 kWh</span>
              <span style={{ color: "var(--ok)" }}>+3.1% vs modelled</span>
            </div>
          </div>
          <div
            className="absolute right-44 top-72 w-[260px] h-[120px] rounded-2xl bg-white p-4"
            style={{ boxShadow: "var(--shadow-lg)", border: "1px solid var(--line)" }}
          >
            <div className="font-mono text-[10px] uppercase tracking-widest muted">
              Quote · QT-2026-0142
            </div>
            <div className="font-display text-xl mt-1" style={{ color: "var(--ink)" }}>
              £18,420 <span className="text-sm muted">incl. VAT</span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: "var(--ok)" }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-xs font-semibold" style={{ color: "var(--ok)" }}>
                accepted 2 min ago
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Features ---------- */}
      <section
        id="features"
        className="px-6 md:px-12 lg:px-20 py-20"
        style={{ background: "var(--surface)" }}
      >
        <div className="max-w-2xl">
          <div
            className="font-mono text-[10px] uppercase tracking-widest"
            style={{ color: "var(--accent-hover)" }}
          >
            What&apos;s inside
          </div>
          <h2
            className="font-display mt-3 text-3xl sm:text-4xl"
            style={{ color: "var(--ink)" }}
            data-testid="landing-features-title"
          >
            Metering-led ops for solar EPCs and asset managers.
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="landing-features">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="bg-white rounded-2xl p-6 transition-all hover:translate-y-[-2px]"
                style={{ border: "1px solid var(--line)", boxShadow: "var(--shadow-sm)" }}
                data-testid={`landing-feature-${f.title.split(" ")[0].toLowerCase()}`}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: "var(--surface-2)", color: "var(--accent-hover)" }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-display text-lg" style={{ color: "var(--ink)" }}>
                  {f.title}
                </div>
                <p className="mt-2 text-sm leading-relaxed muted">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className="px-6 md:px-12 lg:px-20 pb-24">
        <div
          className="rounded-3xl px-8 py-14 sm:py-20 text-center"
          style={{
            background:
              "linear-gradient(135deg, #314041 0%, #00D6DF 100%)",
            boxShadow: "var(--shadow-lg)",
          }}
          data-testid="landing-final-cta"
        >
          <h2 className="font-display text-3xl sm:text-4xl text-white max-w-2xl mx-auto">
            Stop reconciling meter data in spreadsheets.
          </h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto">
            Book a 20-minute walkthrough and we&apos;ll wire one of your live meters into a Flow dashboard on the call — bring a MeterOnline login or a PV*SOL yield report.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <a
              href="mailto:hello@eneprox.co.uk?subject=FLOW%20-%20Book%20a%20demo"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold"
              style={{ background: "#fff", color: "var(--ink)" }}
              data-testid="landing-final-demo"
            >
              Book a demo <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white border border-white/40 hover:bg-white/10 transition-colors"
              data-testid="landing-final-signin"
            >
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="px-6 md:px-12 lg:px-20 py-10 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo size={24} />
            <span className="font-display text-sm" style={{ color: "var(--ink)" }}>
              {brand}
            </span>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest muted">
            © {new Date().getFullYear()} app.eneprox.co.uk · self-hosted solar PV ops
          </div>
        </div>
      </footer>
    </div>
  );
}
